import {Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack} from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {FolioPayment} from "crqlar-react-common/dist/models/billing/folio.model";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

interface FolioPaymentLogsDialogProps {
    open: boolean;
    onClose: () => void;
    payment: FolioPayment | null;
}

const FolioPaymentDetailsDialog = (props: FolioPaymentLogsDialogProps) => {
    const {t} = useTranslation();
    const {open, onClose, payment} = props;
    const [showSetupIntents, setShowSetupIntents] = React.useState(false);
    const [showFiasLogs, setShowFiasLogs] = React.useState(false);

    const renderPaymentDetails = () => {
        if (!payment) return null;

        const detailItems = [
            {label: t('Payment Type'), value: payment.paymentType},
            {label: t('Provider'), value: payment.provider},
            {
                label: t('Amount'),
                value: payment.amount?.amount ? `${payment.amount.amount}${payment.amount.isPercentage ? '%' : ''} ${payment.amount.currency || ''}` : null
            },
            {label: t('Status'), value: payment.status},
            {label: t('Transaction ID'), value: payment.transactionId},
            {label: t('Transaction Date'), value: payment.transactionDate},
            {label: t('Deadline Day'), value: payment.deadlineDay},
            {label: t('Payment Method ID'), value: payment.paymentMethodId},
            {label: t('Room ID'), value: payment.roomId},
            {label: t('Email'), value: payment.email},
            {label: t('First Name'), value: payment.firstName},
            {label: t('Last Name'), value: payment.lastName},
            {label: t('Send Email'), value: payment.sendEmail !== undefined ? payment.sendEmail.toString() : null},
            {
                label: t('Charge Immediately'),
                value: payment.chargeImmediately !== undefined ? payment.chargeImmediately.toString() : null
            },
            {label: t('Stripe Setup Intent'), value: payment.stripeSetupIntentDTO ? '✓' : null},
            {label: t('Hobex Setup Intent'), value: payment.hobexSetupIntentDTO ? '✓' : null},
            {label: t('FIAS Posting Logs'), value: payment.fiasPostingLogs?.length ? `${payment.fiasPostingLogs.length} logs` : null},
            {label: t('Notes'), value: payment.notes},
        ].filter(item => item.value !== null && item.value !== undefined && item.value !== '');

        const renderFiasLogs = () => {
            if (!payment.fiasPostingLogs?.length) return null;

            return (
                <>
                    <Stack direction="row" alignItems="center" spacing={1}
                           onClick={() => setShowFiasLogs(!showFiasLogs)}
                           sx={{cursor: 'pointer', mt: 2, bgcolor: '#fff3e0', p: 1, borderRadius: 1}}>
                        <Typography variant="subtitle1">{t('FIAS Posting Logs')}</Typography>
                        <IconButton size="small">
                            {showFiasLogs ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                        </IconButton>
                    </Stack>

                    <Collapse in={showFiasLogs}>
                        <Stack spacing={2} sx={{mt: 1, pl: 2}}>
                            {payment.fiasPostingLogs.map((log, index) => (
                                <Paper key={index} elevation={0} sx={{p: 2, bgcolor: '#fdf5e6'}}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        {t('FIAS Log')} #{index + 1} - {log.success ? '✅ Success' : '❌ Failed'}
                                    </Typography>
                                    <Stack spacing={1}>
                                        {log.date && (
                                            <Typography variant="body2">
                                                <strong>{t('Date')}:</strong> {new Date(log.date).toLocaleString()}
                                            </Typography>
                                        )}
                                        {log.ref && (
                                            <Typography variant="body2">
                                                <strong>{t('Reference')}:</strong> {log.ref}
                                            </Typography>
                                        )}
                                        {log.amount && (
                                            <Typography variant="body2">
                                                <strong>{t('Amount')}:</strong> {(log.amount / 100).toFixed(2)}
                                            </Typography>
                                        )}
                                        {log.integration && (
                                            <Typography variant="body2">
                                                <strong>{t('Integration')}:</strong> {log.integration}
                                            </Typography>
                                        )}
                                        {log.rawRequest && (
                                            <div>
                                                <Typography variant="body2" sx={{mb: 1}}>
                                                    <strong>{t('Raw Request')}:</strong>
                                                </Typography>
                                                <pre style={{
                                                    backgroundColor: '#f0f0f0',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    overflow: 'auto',
                                                    maxHeight: '100px',
                                                    fontSize: '10px'
                                                }}>
                                                    {log.rawRequest}
                                                </pre>
                                            </div>
                                        )}
                                        {log.rawResponse && (
                                            <div>
                                                <Typography variant="body2" sx={{mb: 1}}>
                                                    <strong>{t('Raw Response')}:</strong>
                                                </Typography>
                                                <pre style={{
                                                    backgroundColor: '#f0f0f0',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    overflow: 'auto',
                                                    maxHeight: '100px',
                                                    fontSize: '10px'
                                                }}>
                                                    {log.rawResponse}
                                                </pre>
                                            </div>
                                        )}
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </Collapse>
                </>
            );
        };

        const renderSetupIntents = () => {
            if (!payment.stripeSetupIntentDTO && !payment.hobexSetupIntentDTO) return null;

            return (
                <>
                    <Stack direction="row" alignItems="center" spacing={1}
                           onClick={() => setShowSetupIntents(!showSetupIntents)}
                           sx={{cursor: 'pointer', mt: 2, bgcolor: '#edf7ff', p: 1, borderRadius: 1}}>
                        <Typography variant="subtitle1">{t('Setup Intent Details')}</Typography>
                        <IconButton size="small">
                            {showSetupIntents ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                        </IconButton>
                    </Stack>

                    <Collapse in={showSetupIntents}>
                        <Stack spacing={2} sx={{mt: 1, pl: 2}}>
                            {payment.stripeSetupIntentDTO && (
                                <Paper elevation={0} sx={{p: 2, bgcolor: '#f5f9ff'}}>
                                    <Typography variant="subtitle2" gutterBottom>{t('Stripe Setup Intent')}</Typography>
                                    <pre style={{
                                        backgroundColor: '#f0f0f0',
                                        padding: '8px',
                                        borderRadius: '4px',
                                        overflow: 'auto',
                                        maxHeight: '150px',
                                        fontSize: '10px'
                                    }}>
                                        {JSON.stringify(payment.stripeSetupIntentDTO, null, 2)}
                                    </pre>
                                </Paper>
                            )}

                            {payment.hobexSetupIntentDTO && (
                                <Paper elevation={0} sx={{p: 2, bgcolor: '#f5f9ff'}}>
                                    <Typography variant="subtitle2" gutterBottom>{t('Hobex Setup Intent')}</Typography>
                                    <pre style={{
                                        backgroundColor: '#f0f0f0',
                                        padding: '8px',
                                        borderRadius: '4px',
                                        overflow: 'auto',
                                        maxHeight: '150px',
                                        fontSize: '10px'
                                    }}>
                                        {JSON.stringify(payment.hobexSetupIntentDTO, null, 2)}
                                    </pre>
                                </Paper>
                            )}
                        </Stack>
                    </Collapse>
                </>
            );
        };

        return (
            <Paper elevation={0} sx={{padding: 2, mb: 2, backgroundColor: '#f9f9f9'}}>
                <Typography variant="h6" gutterBottom>{t('Payment Information')}</Typography>
                <Stack spacing={1}>
                    {detailItems.map((item, index) => (
                        <Stack key={index} direction="row" spacing={2}>
                            <Typography variant="body1" fontWeight="bold" sx={{minWidth: 150}}>
                                {item.label}:
                            </Typography>
                            <Typography variant="body1">
                                {item.value}
                            </Typography>
                        </Stack>
                    ))}

                    {renderSetupIntents()}
                    {renderFiasLogs()}
                </Stack>
            </Paper>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('Payment details')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    {renderPaymentDetails()}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    {t('Close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FolioPaymentDetailsDialog;